"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { useState } from "react";
import { useLocale } from "next-intl";

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";

import Link from "next/link";
import { IconBrandLinkedin, IconBrandX, IconCheck } from "@tabler/icons-react";
import { Button } from "./button";
import { Logo } from "./Logo";

const t = {
  en: {
    title: "Talk to our team",
    subtitle: "Whether you want a demo, have a question about pricing, or need help getting started — we're here.",
    fullName: "Full Name",
    namePlaceholder: "John Smith",
    workEmail: "Work Email",
    emailPlaceholder: "john@company.com",
    company: "Company",
    companyPlaceholder: "Acme Corp",
    teamSize: "Team Size",
    teamSizePlaceholder: "Select size",
    teamSizes: [
      { value: "1-5", label: "1 – 5 reps" },
      { value: "6-20", label: "6 – 20 reps" },
      { value: "21-50", label: "21 – 50 reps" },
      { value: "51-200", label: "51 – 200 reps" },
      { value: "200+", label: "200+ reps" },
    ],
    interest: "I'm interested in",
    interestPlaceholder: "Select topic",
    interests: [
      { value: "demo", label: "Book a Demo" },
      { value: "pricing", label: "Pricing" },
      { value: "trial", label: "Starting a Trial" },
      { value: "support", label: "Technical Support" },
      { value: "partnership", label: "Partnership" },
      { value: "other", label: "Other" },
    ],
    message: "Message",
    messagePlaceholder: "Tell us about your team and what you're looking to achieve with Coldfire...",
    submit: "Send message",
    followUs: "Follow us",
    successTitle: "Message sent!",
    successBody: "Thanks for reaching out. Our team will get back to you within one business day.",
  },
  de: {
    title: "Sprechen Sie mit unserem Team",
    subtitle: "Egal ob Sie eine Demo möchten, Fragen zu Preisen haben oder Hilfe beim Einstieg benötigen — wir sind für Sie da.",
    fullName: "Vollständiger Name",
    namePlaceholder: "Max Mustermann",
    workEmail: "Geschäfts-E-Mail",
    emailPlaceholder: "max@unternehmen.de",
    company: "Unternehmen",
    companyPlaceholder: "Muster GmbH",
    teamSize: "Teamgröße",
    teamSizePlaceholder: "Größe auswählen",
    teamSizes: [
      { value: "1-5", label: "1 – 5 Mitarbeiter" },
      { value: "6-20", label: "6 – 20 Mitarbeiter" },
      { value: "21-50", label: "21 – 50 Mitarbeiter" },
      { value: "51-200", label: "51 – 200 Mitarbeiter" },
      { value: "200+", label: "200+ Mitarbeiter" },
    ],
    interest: "Ich interessiere mich für",
    interestPlaceholder: "Thema auswählen",
    interests: [
      { value: "demo", label: "Demo buchen" },
      { value: "pricing", label: "Preise" },
      { value: "trial", label: "Testversion starten" },
      { value: "support", label: "Technischer Support" },
      { value: "partnership", label: "Partnerschaft" },
      { value: "other", label: "Sonstiges" },
    ],
    message: "Nachricht",
    messagePlaceholder: "Erzählen Sie uns von Ihrem Team und was Sie mit Coldfire erreichen möchten...",
    submit: "Nachricht senden",
    followUs: "Folgen Sie uns",
    successTitle: "Nachricht gesendet!",
    successBody: "Vielen Dank für Ihre Nachricht. Unser Team wird sich innerhalb eines Werktages bei Ihnen melden.",
  },
};

const formSchema = z.object({
  name: z.string().min(1, "Please enter your name"),
  email: z.string().email("Please enter a valid email").min(1, "Please enter your email"),
  company: z.string().min(1, "Please enter your company name"),
  teamSize: z.string().min(1, "Please select your team size"),
  interest: z.string().min(1, "Please select what you're interested in"),
  message: z.string().min(1, "Please enter your message"),
});

type ContactFormValues = z.infer<typeof formSchema>;

export function ContactForm() {
  const locale = useLocale();
  const c = locale === "de" ? t.de : t.en;

  const [submitted, setSubmitted] = useState(false);

  const form = useForm<ContactFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: { name: "", email: "", company: "", teamSize: "", interest: "", message: "" },
  });

  async function onSubmit(values: ContactFormValues) {
    console.log("Contact form submitted", values);
    setSubmitted(true);
  }

  const inputClass =
    "block w-full bg-white dark:bg-neutral-900 px-4 rounded-md border-0 py-2 shadow-aceternity text-black placeholder:text-gray-400 focus:ring-2 focus:ring-neutral-400 focus:outline-none sm:text-sm sm:leading-6 dark:text-white";

  const labelClass =
    "block text-sm font-medium leading-6 text-neutral-700 dark:text-muted-dark";

  const socials = [
    {
      title: "Twitter / X",
      href: "#",
      icon: <IconBrandX className="h-5 w-5 text-muted dark:text-muted-dark hover:text-black dark:hover:text-white transition-colors" />,
    },
    {
      title: "LinkedIn",
      href: "#",
      icon: <IconBrandLinkedin className="h-5 w-5 text-muted dark:text-muted-dark hover:text-black dark:hover:text-white transition-colors" />,
    },
  ];

  if (submitted) {
    return (
      <div className="flex relative z-20 items-center w-full justify-center px-4 py-4 lg:py-12 sm:px-6 lg:flex-none lg:px-20 xl:px-24">
        <div className="mx-auto w-full max-w-md text-center">
          <div className="flex items-center justify-center mb-6">
            <div className="h-16 w-16 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
              <IconCheck className="h-8 w-8 text-green-600 dark:text-green-400" />
            </div>
          </div>
          <h2 className="text-2xl font-bold text-black dark:text-white mb-3">
            {c.successTitle}
          </h2>
          <p className="text-neutral-500 dark:text-neutral-400 text-sm">
            {c.successBody}
          </p>
        </div>
      </div>
    );
  }

  return (
    <Form {...form}>
      <div className="flex relative z-20 items-center w-full justify-center px-4 py-4 lg:py-12 sm:px-6 lg:flex-none lg:px-20 xl:px-24">
        <div className="mx-auto w-full max-w-md">
          <div className="mb-8">
            <Logo />
            <h1 className="mt-6 text-2xl font-bold leading-9 tracking-tight text-black dark:text-white">
              {c.title}
            </h1>
            <p className="mt-2 text-sm text-neutral-500 dark:text-neutral-400 max-w-sm">
              {c.subtitle}
            </p>
          </div>

          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <label htmlFor="name" className={labelClass}>{c.fullName}</label>
                    <FormControl>
                      <div className="mt-1">
                        <input id="name" type="text" placeholder={c.namePlaceholder} className={inputClass} {...field} />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <label htmlFor="email" className={labelClass}>{c.workEmail}</label>
                    <FormControl>
                      <div className="mt-1">
                        <input id="email" type="email" placeholder={c.emailPlaceholder} className={inputClass} {...field} />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="company"
              render={({ field }) => (
                <FormItem>
                  <label htmlFor="company" className={labelClass}>{c.company}</label>
                  <FormControl>
                    <div className="mt-1">
                      <input id="company" type="text" placeholder={c.companyPlaceholder} className={inputClass} {...field} />
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="teamSize"
                render={({ field }) => (
                  <FormItem>
                    <label htmlFor="teamSize" className={labelClass}>{c.teamSize}</label>
                    <FormControl>
                      <div className="mt-1">
                        <select id="teamSize" className={inputClass + " cursor-pointer"} {...field}>
                          <option value="" disabled>{c.teamSizePlaceholder}</option>
                          {c.teamSizes.map((s) => (
                            <option key={s.value} value={s.value}>{s.label}</option>
                          ))}
                        </select>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="interest"
                render={({ field }) => (
                  <FormItem>
                    <label htmlFor="interest" className={labelClass}>{c.interest}</label>
                    <FormControl>
                      <div className="mt-1">
                        <select id="interest" className={inputClass + " cursor-pointer"} {...field}>
                          <option value="" disabled>{c.interestPlaceholder}</option>
                          {c.interests.map((i) => (
                            <option key={i.value} value={i.value}>{i.label}</option>
                          ))}
                        </select>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="message"
              render={({ field }) => (
                <FormItem>
                  <label htmlFor="message" className={labelClass}>{c.message}</label>
                  <FormControl>
                    <div className="mt-1">
                      <textarea
                        rows={4}
                        id="message"
                        placeholder={c.messagePlaceholder}
                        className={inputClass + " resize-none"}
                        {...field}
                      />
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <Button className="w-full" type="submit">
              {c.submit}
            </Button>
          </form>

          <div className="flex items-center gap-4 mt-8 pt-6 border-t border-neutral-100 dark:border-neutral-800">
            <span className="text-xs text-neutral-400">{c.followUs}</span>
            {socials.map((social) => (
              <Link href={social.href} key={social.title} aria-label={social.title}>
                {social.icon}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </Form>
  );
}
